import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { Calendar } from '@app/lib';
import { AppStore } from '@app/models';
import { CALENDAR_TYPE, CustomDateObject } from '@app/models/Calendar';
import { DateProvider, StoreProvider } from '@app/providers';
import { LanguageService } from '@app/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-calendar-default',
    templateUrl: './default-calendar.component.html',
    styleUrls: ['./default-calendar.component.scss'],
})
export class DefaultCalendarComponent implements AfterViewInit, OnDestroy {
    @ViewChild('calendarContainer') calendarContainer!: ElementRef;

    @Input() dates?: CustomDateObject;
    @Output() dateSelect: EventEmitter<CustomDateObject> = new EventEmitter();

    private $storeSub!: Subscription;
    private app?: AppStore;
    private calendar!: Calendar;

    constructor(
        private dateProvider: DateProvider,
        private storeProvider: StoreProvider,
        private $languageService: LanguageService,
        private translate: TranslateService
    ) {}

    ngAfterViewInit(): void {
        this.$storeSub = this.storeProvider
            .listen('app')
            .subscribe((app: AppStore) => {
                this.app = app;
                const lang = this.$languageService.getCurrentLang();
                const isUS = lang === 'en';

                const i18n = {
                    previousMonth: this.translate.instant(
                        'calendar.previousMonth'
                    ),
                    nextMonth: this.translate.instant('calendar.nextMonth'),
                    months: this.translate.instant('calendar.months'),
                    weekdays: this.translate.instant('calendar.weekdays'),
                    weekdaysShort: this.translate.instant(
                        'calendar.weekdaysShort'
                    ),
                };

                // listen('app') réémet à chaque mise à jour du store : sans détruire
                // l'instance précédente, deux Pikaday sur le même champ se renvoient
                // l'événement change en boucle (dépassement de pile).
                this.calendar?.destroy();
                this.calendar = new Calendar(
                    this.calendarContainer.nativeElement,
                    {
                        format: isUS ? 'dddd MM/DD/YYYY' : 'dddd DD/MM/YYYY',
                        bound: false,
                        theme: `custom-calendar-theme ${
                            this.getTheme()[this.dates?.select as never]
                        }`,
                        onSelect: this.onSelect.bind(this),
                        setDefaultDate: true,
                        defaultDate:
                            this.dates?.current?.start.asMoment.toDate(),
                        maxDate: moment().toDate(),
                        minDate:
                            app.props?.selectedLocalBusiness?.instalationDate?.asMoment.toDate(),
                        firstDay: isUS ? 0 : 1,
                        showDaysInNextAndPreviousMonths: true,
                        pickWholeWeek: this.dates?.select === 'week',
                        i18n,
                    }
                );
            });
    }

    private getTheme() {
        return {
            [CALENDAR_TYPE.DAY]: '',
            [CALENDAR_TYPE.WEEK]: '',
            [CALENDAR_TYPE.MONTH]: '',
            [CALENDAR_TYPE.YEAR]: '',
        };
    }

    private onSelect(date: Date) {
        this.calendar.destroy();
        this.dateSelect.emit(this.fromCalendarToEvent(date));
    }

    private fromCalendarToEvent = (date?: Date) => {
        const instanciedDate = moment(date);

        const range = this.dateProvider.onDateSelect(
            instanciedDate,
            this.dates?.select as string,
            this.dates?.type as string,
            true
        );

        if (
            this.dates?.type === 'month' &&
            (this.app?.props?.dates?.select === 'day' ||
                this.app?.props?.dates?.select === 'week')
        ) {
            range.versus = {
                start: range.current.start.clone().subtract(28, 'day'),
                end: range.current.end.clone().subtract(28, 'day'),
            };
        }

        return {
            current: {
                start: this.dateProvider.fromDateToStore(range.current.start),
                end: this.dateProvider.fromDateToStore(range.current.end, true),
            },
            versus: {
                start: this.dateProvider.fromDateToStore(range.versus.start),
                end: this.dateProvider.fromDateToStore(range.versus.end, true),
            },
        };
    };

    public goToToday() {
        this.calendar.goToday();
    }

    ngOnDestroy(): void {
        this.$storeSub?.unsubscribe();
        this.calendar?.destroy();
    }
}
